import { hasPermission, sessionHasPermission, type PainelPermission, type PainelRole } from '@/lib/painel-rbac';

export type CmsPublicationStatus = 'draft' | 'pending_review' | 'published';

export const PUBLICATION_STATUS_LABELS: Record<CmsPublicationStatus, string> = {
  draft: 'Rascunho',
  pending_review: 'Em revisão',
  published: 'Publicado',
};

export const PUBLICATION_STATUS_BADGE_CLASS: Record<CmsPublicationStatus, string> = {
  draft: 'bg-amber-500/15 text-amber-200 ring-amber-400/25',
  pending_review: 'bg-violet-500/15 text-violet-200 ring-violet-400/25',
  published: 'bg-emerald-500/15 text-emerald-200 ring-emerald-400/25',
};

/** Conteúdo visível no site público (undefined = legado publicado). */
export function isPubliclyVisible(status?: CmsPublicationStatus): boolean {
  return !status || status === 'published';
}

export function canPublishContent(role: PainelRole | null | undefined): boolean {
  if (!role) return false;
  if (role === 'super_admin' || role === 'admin') return true;
  return (
    hasPermission(role, 'guides.publish') ||
    hasPermission(role, 'powers.publish') ||
    hasPermission(role, 'aces.publish')
  );
}

export function canAccessReviewQueue(role: PainelRole | null | undefined): boolean {
  if (!role) return false;
  if (role === 'super_admin' || role === 'admin') return true;
  return (
    hasPermission(role, 'guides.review') ||
    hasPermission(role, 'powers.review') ||
    hasPermission(role, 'aces.review')
  );
}

/** Resolve estado ao guardar guia (switch «publicar» ligado). */
export function resolveGuideStatusOnSave(
  role: PainelRole | null | undefined,
  wantsPublication: boolean
): CmsPublicationStatus {
  if (!wantsPublication) return 'draft';
  return canPublishContent(role) ? 'published' : 'pending_review';
}

export function canSendGuideToReview(role: PainelRole | null | undefined): boolean {
  return canAccessReviewQueue(role);
}

/** Badge «Artigo Popular» — apenas Super Admin e Admin (permissão guides.featured). */
export function canFeatureGuide(
  session: { role: PainelRole; permissions?: PainelPermission[] } | null | undefined
): boolean {
  if (!session) return false;
  return sessionHasPermission(session, 'guides.featured');
}

export function canDeleteGuide(
  session: { role: PainelRole; permissions?: PainelPermission[] } | null | undefined
): boolean {
  if (!session) return false;
  return sessionHasPermission(session, 'guides.delete');
}

export function canRestoreGuide(
  session: { role: PainelRole; permissions?: PainelPermission[] } | null | undefined
): boolean {
  return canDeleteGuide(session);
}

export function canForceDeleteGuide(
  session: { role: PainelRole; permissions?: PainelPermission[] } | null | undefined
): boolean {
  if (!session) return false;
  if (session.role === 'super_admin') return true;
  return sessionHasPermission(session, 'guides.force_delete');
}

/** Powers e Aces ao guardar no painel. */
export function resolvePublicationOnSave(role: PainelRole | null | undefined): CmsPublicationStatus {
  return canPublishContent(role) ? 'published' : 'pending_review';
}
